/* SymptomTrackerWidget — 5-секундно daily дневник + correlation insights
 *
 * UI: 4 slider-like star rows (Hot flashes / Sleep / Mood / Energy, 0-5)
 *     → onChange дебоунс записва. Today-state показва current values.
 *
 * Correlation panel: след ≥5 дни логнати данни, показва insights:
 *    "Средиземноморските дни намаляват hot flashes с 34% (p<0.05)"
 *
 * Auto-shown само ако user има assigned phenotype с research record.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Thermometer, Moon, Smile, Zap, TrendingUp, TrendingDown, Activity, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

const SYMPTOMS = [
  { key: "hot_flashes", label: "Hot flashes", icon: Thermometer, color: "#EF4444", is_negative: true },
  { key: "sleep_quality", label: "Сън", icon: Moon, color: "#8B5CF6", is_negative: false },
  { key: "mood", label: "Настроение", icon: Smile, color: "#F59E0B", is_negative: false },
  { key: "energy", label: "Енергия", icon: Zap, color: "#10B981", is_negative: false },
];

function Scale0to5({ value, onChange, color, testId }) {
  return (
    <div className="flex gap-1" data-testid={testId}>
      {[0, 1, 2, 3, 4, 5].map((n) => {
        const active = value !== null && value !== undefined && value >= n && n > 0;
        const isZero = n === 0 && value === 0;
        return (
          <button key={n} onClick={() => onChange(n)}
            className={`w-6 h-6 rounded-md text-[10px] font-bold transition-all ${
              active || isZero ? "text-white shadow-sm" : "bg-gray-100 dark:bg-white/5 text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10"
            }`}
            style={active || isZero ? { backgroundColor: color } : undefined}
            data-testid={`${testId}-${n}`}>
            {n}
          </button>
        );
      })}
    </div>
  );
}

function SymptomRow({ symptom, value, onChange, isDark }) {
  const Icon = symptom.icon;
  return (
    <div className="flex items-center gap-2" data-testid={`symptom-row-${symptom.key}`}>
      <div className="shrink-0 flex items-center gap-1.5 w-28">
        <Icon className="w-3.5 h-3.5" style={{ color: symptom.color }} />
        <span className={`text-[11px] ${isDark ? "text-gray-300" : "text-gray-700"}`}>{symptom.label}</span>
      </div>
      <Scale0to5 value={value} onChange={onChange} color={symptom.color} testId={`symptom-scale-${symptom.key}`} />
    </div>
  );
}

function InsightsPanel({ API, refreshKey }) {
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data: resp } = await axios.get(`${API}/symptoms/correlate?days=30`);
        if (!cancel) setData(resp);
      } catch {
        if (!cancel) setData(null);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [API, refreshKey]);

  if (loading) return null;
  if (!data?.supported) {
    const need = data?.min_required || 5;
    const have = data?.entries_logged ?? data?.days_with_meals ?? 0;
    return (
      <div className={`rounded-md p-2 text-[10px] italic ${isDark ? "bg-white/5 text-gray-400" : "bg-gray-50 text-gray-500"}`}
        data-testid="insights-not-ready">
        📊 Нуждаем се от поне {need} дни с логнати симптоми + храни за персонална корелация. Текущо: {have} дни.
      </div>
    );
  }

  const insights = data.insights || [];
  if (insights.length === 0) return null;

  return (
    <div className="space-y-1.5" data-testid="insights-panel">
      <div className={`text-[9px] uppercase tracking-wider font-semibold flex items-center gap-1 ${isDark ? "text-sky-400" : "text-sky-700"}`}>
        <TrendingUp className="w-3 h-3" />
        Персонални корелации ({data.days_analyzed} дни; медиана coverage {data.median_coverage}%)
      </div>
      {insights.map((ins) => {
        const positive = ins.direction === "improves";
        const TrendIcon = positive ? TrendingDown : TrendingUp;  // за hot_flashes: drop = good
        const showTrend = ins.is_negative ? (positive ? TrendingDown : TrendingUp) : (positive ? TrendingUp : TrendingDown);
        const tone = positive
          ? (isDark ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/20" : "bg-emerald-50 text-emerald-800 border-emerald-200")
          : (isDark ? "bg-rose-500/10 text-rose-300 border-rose-500/20" : "bg-rose-50 text-rose-800 border-rose-200");
        const deltaStr = ins.pct_delta !== null && ins.pct_delta !== undefined
          ? `${ins.pct_delta > 0 ? "+" : ""}${ins.pct_delta}%`
          : "—";
        const sigBadge = ins.significant ? " • p<0.05" : ins.p_value < 0.1 ? " • p<0.10 trend" : "";
        return (
          <div key={ins.symptom}
            className={`rounded p-2 border text-[10px] leading-snug flex items-start gap-2 ${tone}`}
            data-testid={`insight-${ins.symptom}`}>
            {React.createElement(showTrend, { className: "w-3 h-3 shrink-0 mt-0.5" })}
            <div className="flex-1">
              <span className="font-semibold">{ins.symptom_bg}</span> — high-coverage дни {ins.mean_high_coverage_days}/5 vs low {ins.mean_low_coverage_days}/5
              <span className="font-bold ml-1">({deltaStr}{sigBadge})</span>
              <span className={`ml-1 text-[9px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                · n={ins.n_high}/{ins.n_low}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function SymptomTrackerWidget({ refreshKey = 0 }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [entry, setEntry] = useState({ hot_flashes: null, sleep_quality: null, mood: null, energy: null });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveTimer, setSaveTimer] = useState(null);
  const [hasPhenotype, setHasPhenotype] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [insightsKey, setInsightsKey] = useState(0);

  // Check if user has phenotype (widget e useful само при assigned phenotype)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pathway-score/my-phenotype`);
        setHasPhenotype(!!data?.has_research);
      } catch {
        setHasPhenotype(false);
      }
    })();
  }, [API]);

  // Load today's entry
  const loadToday = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/symptoms/today`);
      if (data?.entry) {
        setEntry({
          hot_flashes: data.entry.hot_flashes ?? null,
          sleep_quality: data.entry.sleep_quality ?? null,
          mood: data.entry.mood ?? null,
          energy: data.entry.energy ?? null,
        });
      }
      setLoaded(true);
    } catch {
      setLoaded(true);
    }
  }, [API]);

  useEffect(() => { if (hasPhenotype) loadToday(); }, [loadToday, hasPhenotype, refreshKey]);

  const logged_count = useMemo(
    () => SYMPTOMS.filter((s) => entry[s.key] !== null && entry[s.key] !== undefined).length,
    [entry]
  );

  const save = useCallback(async (newEntry) => {
    setSaving(true);
    try {
      await axios.post(`${API}/symptoms/log`, newEntry);
      setInsightsKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при запис");
    } finally {
      setSaving(false);
    }
  }, [API]);

  const setField = (k, v) => {
    const next = { ...entry, [k]: v };
    setEntry(next);
    if (saveTimer) clearTimeout(saveTimer);
    const t = setTimeout(() => save(next), 500);  // debounce 500ms
    setSaveTimer(t);
  };

  if (hasPhenotype === null || !hasPhenotype) return null;

  return (
    <div className={`rounded-lg overflow-hidden border ${isDark ? "border-pink-500/20 bg-pink-500/5" : "border-pink-200 bg-pink-50/40"}`}
      data-testid="symptom-tracker-widget">
      <button onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-black/5 transition-colors"
        data-testid="symptom-tracker-header">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isDark ? "bg-pink-500/20" : "bg-pink-100"}`}>
          <Activity className={`w-4 h-4 ${isDark ? "text-pink-300" : "text-pink-600"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? "text-pink-300" : "text-pink-700"}`}>
            Дневник на симптоми
          </div>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              {logged_count > 0 ? `Днес: ${logged_count}/4 логнати` : "Днес: няма запис"}
            </span>
            <span className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              · 5 секунди, само кликове
            </span>
            {saving && <span className={`text-[9px] italic ${isDark ? "text-gray-500" : "text-gray-400"}`}>записва...</span>}
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {expanded && loaded && (
        <div className={`px-3 pb-3 space-y-3 border-t ${isDark ? "border-white/10" : "border-gray-200"}`}>
          <div className="pt-2.5 space-y-2">
            {SYMPTOMS.map((s) => (
              <SymptomRow key={s.key} symptom={s} value={entry[s.key]} onChange={(v) => setField(s.key, v)} isDark={isDark} />
            ))}
          </div>
          <div className={`text-[9px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
            0 = няма / най-лошо · 5 = най-силно / най-добре. Записът се запазва автоматично.
          </div>
          <InsightsPanel API={API} refreshKey={insightsKey + refreshKey} />
        </div>
      )}
    </div>
  );
}
